import { useConfirm } from '@genial/components';
import { Utils, useBreakpointValue } from '@genial/utils';
import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { useRecoilQuery } from '../../../hooks/useRecoilQuery';
import { useBatchTicketInvestLinkActions } from '../../../store/batchTicketInvestLink/action';
import {
  authorizationTokenState,
  customerOrderIdState,
} from '../../../store/batchTicketInvestLink/atom';
import { customersBasketOffersQuery } from '../../../store/batchTicketInvestLink/selectors/customerBasketOffers';
import { whiteLabelByBasketCustomerIdQuery } from '../../../store/whitelabel/seletor';
import { OperationType } from '../../BatchTicket/components/DynamicAsset/contexts/constants';
import { confirmOrderData, getRefusalOrderData } from '../Constants';
import { OffersInfoDataProps, ProductsDataProps } from '../utils/types';

export interface BatchTicketInvestLinkResponse {
  message: string;
  status: number;
  Data: ProductsDataProps[];
}

const emptyData = {
  offers: [],
  basketAdvisorOrderHash: '',
  totalCustomerOffersAmount: '',
  totalCustomerOffers: 0,
  customerName: '',
  advisorName: '',
  createDate: '',
  operation: '',
  operationType: 'sell' as OperationType,
};

export const useBatchTicketInvestLink = () => {
  const { patchCustomerOffers, refuseCustomerOffer } = useBatchTicketInvestLinkActions();

  const customerOrderId = useRecoilValue(customerOrderIdState);
  const authorizationToken = useRecoilValue(authorizationTokenState);
  const [isConfirmedOrder, setIsConfirmedOrder] = useState(false);
  const [dataOffers, setDataOffers] = useState<ProductsDataProps[]>([]);

  const { confirm } = useConfirm();

  const { data: whatsAppLinkData, loading: whatsAppLinkIsLoading } = useRecoilQuery(
    whiteLabelByBasketCustomerIdQuery({
      basketCustomerId: customerOrderId,
      jwtToken: authorizationToken.jwtToken,
    })
  );

  const {
    data: response,
    error,
    loading: isLoading,
    refresher,
  } = useRecoilQuery(
    customersBasketOffersQuery({
      basketCustomerId: customerOrderId,
      basketOrderStatus: ['CustomerPendingAcceptance', 'CanceledByCustomer'],
      jwtToken: authorizationToken.jwtToken,
    })
  );

  const totalCustomerOffersAmount = dataOffers
    .filter((offer) => offer.acceptance)
    .reduce((sum, offer) => sum + offer.amount, 0);

  const { offers, ...dataWithoutOffers } = response?.data || emptyData;

  const isMobile = useBreakpointValue({ base: true, sm: false });

  const confirmModalProps = {
    isMobile,
    ...confirmOrderData,
  };

  const refusalModalProps = {
    isMobile,
    hideIcon: true,
    ...getRefusalOrderData(dataWithoutOffers.operationType === 'sell'),
  };

  const mappedOffersRequest = (data: ProductsDataProps[]) => {
    const mappedOffers = data.map((offer) => ({
      basketCustomerOfferId: offer.id,
      orderType: offer.orderType,
      marketType: offer.marketType,
      amount: offer.amount,
      quantity: offer.quantity,
      requestedQuantity: offer.requestedQuantity,
      requestedAmount: offer.requestedAmount,
      requestedPrice: offer.requestedPrice,
      billingTypeId: offer.billingTypeId,
      statusId: offer.acceptance ? 2005 : 2006,
    }));

    return mappedOffers;
  };

  const confirmOrder = async () => {
    const mappedOffers = mappedOffersRequest(dataOffers);

    await patchCustomerOffers({
      basketCustomerOrderId: customerOrderId,
      customerOffers: mappedOffers,
      jwtToken: authorizationToken.jwtToken,
    });
    setIsConfirmedOrder(true);
  };

  const handleRedirectWhatsapp = (target = '_blank') => {
    if (isMobile) {
      window.location.href = whatsAppLinkData as string;
    } else {
      window.open(whatsAppLinkData, target);
    }
  };

  const refuseOrder = async () => {
    await refuseCustomerOffer({
      basketCustomerOrderId: customerOrderId,
      jwtToken: authorizationToken.jwtToken,
    });
    handleRedirectWhatsapp('_self');
  };

  const handleConfirmOrder = () => {
    confirm(confirmModalProps, () => {
      confirmOrder();
    });
  };

  const handleRefuseOrder = () => {
    confirm(refusalModalProps, () => {
      refuseOrder();
    });
  };

  const headerOrderData = [
    {
      label: 'Quantidade ativos',
      value: dataWithoutOffers.totalCustomerOffers,
    },
    {
      label: 'Assessor',
      value: dataWithoutOffers.advisorName,
    },
    {
      label: 'Validade',
      value: dataWithoutOffers.createDate,
    },
    {
      label: 'Operação',
      value: dataWithoutOffers.operation,
    },
  ];

  const offersInfoData = {
    ...dataWithoutOffers,
    totalCustomerOffersAmount: Utils.Mask.Money(totalCustomerOffersAmount),
    headerOrderData,
  } as OffersInfoDataProps;

  const handleToggleOrderStatus = (id: string) => {
    setDataOffers((prevOffers) =>
      prevOffers.map((offer) =>
        offer.id === id ? { ...offer, acceptance: !offer.acceptance } : offer
      )
    );
  };

  useEffect(() => {
    if (!offers) {
      return;
    }

    setDataOffers(offers);
  }, [offers]);

  return {
    retryBatchTicketInvestLink: refresher,
    handleConfirmOrder,
    handleRefuseOrder,
    handleToggleOrderStatus,
    handleRedirectWhatsapp,
    listBatchTicketLink: dataOffers,
    errorBatchTicketLink: error,
    loadingBatchTicketLink: isLoading || whatsAppLinkIsLoading,
    offersInfoData,
    isConfirmedOrder,
  };
};
